<template>
  <div v-if="visible" class="oa-modal-overlay" @click.self="close">
    <div class="oa-modal oa-modal-sm">
      <div class="oa-modal-header">
        <h3>
          <i class="fa fa-link oa-modal-icon"></i>
          <span>{{ label || 'Booking Link' }}</span>
        </h3>
        <button class="oa-modal-close" @click="close">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div class="oa-modal-body">
        <!-- Short URL display -->
        <div class="oa-form-group">
          <label class="oa-form-label">Short URL</label>
          <div class="utm-short-url-display">
            <span class="utm-short-url-text">{{ shortUrl }}</span>
          </div>
        </div>

        <!-- Campaign Tracking section -->
        <div class="utm-section-divider">
          <span>Campaign Tracking (optional)</span>
        </div>

        <!-- Source -->
        <div class="oa-form-group">
          <label class="oa-form-label">Source</label>
          <input type="text" class="oa-form-input" v-model="utmSource" placeholder="e.g. google, facebook, email" />
          <div class="utm-chip-row">
            <button
              v-for="chip in sourceChips"
              :key="chip"
              class="utm-chip"
              :class="{ 'utm-chip-active': utmSource === chip }"
              @click="toggleChip('utmSource', chip)"
            >{{ chip }}</button>
          </div>
        </div>

        <!-- Medium -->
        <div class="oa-form-group">
          <label class="oa-form-label">Medium</label>
          <input type="text" class="oa-form-input" v-model="utmMedium" placeholder="e.g. cpc, social, email" />
          <div class="utm-chip-row">
            <button
              v-for="chip in mediumChips"
              :key="chip"
              class="utm-chip"
              :class="{ 'utm-chip-active': utmMedium === chip }"
              @click="toggleChip('utmMedium', chip)"
            >{{ chip }}</button>
          </div>
        </div>

        <!-- Campaign -->
        <div class="oa-form-group">
          <label class="oa-form-label">Campaign</label>
          <input type="text" class="oa-form-input" v-model="utmCampaign" placeholder="e.g. spring-2026, new-patient-promo" />
        </div>

        <!-- Content -->
        <div class="oa-form-group">
          <label class="oa-form-label">Content</label>
          <input type="text" class="oa-form-input" v-model="utmContent" placeholder="e.g. banner-ad, sidebar-link (for A/B testing)" />
        </div>

        <!-- Preview -->
        <div class="oa-form-group">
          <label class="oa-form-label">Link Preview</label>
          <div class="utm-preview-box">
            <span class="utm-preview-url">{{ previewUrl }}</span>
          </div>
        </div>
      </div>

      <div class="oa-modal-footer">
        <button class="oa-btn oa-btn-secondary" @click="close">Close</button>
        <button class="oa-btn oa-btn-primary" @click="copyLink">
          <i :class="copied ? 'fa fa-check' : 'fa fa-clipboard'"></i>
          {{ copied ? 'Copied!' : 'Copy Link' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'UtmLinkBuilder',
  props: {
    shortUrl: { type: String, required: true },
    label: { type: String, default: '' },
    visible: { type: Boolean, default: false }
  },

  data() {
    return {
      utmSource: '',
      utmMedium: '',
      utmCampaign: '',
      utmContent: '',
      copied: false,
      sourceChips: ['google', 'facebook', 'instagram', 'yelp', 'email', 'print'],
      mediumChips: ['cpc', 'social', 'organic', 'email', 'print', 'referral']
    };
  },

  computed: {
    previewUrl() {
      if (!this.shortUrl) return '';
      const params = new URLSearchParams();
      if (this.utmSource.trim()) params.set('utm_source', this.utmSource.trim());
      if (this.utmMedium.trim()) params.set('utm_medium', this.utmMedium.trim());
      if (this.utmCampaign.trim()) params.set('utm_campaign', this.utmCampaign.trim());
      if (this.utmContent.trim()) params.set('utm_content', this.utmContent.trim());
      const qs = params.toString();
      return qs ? `${this.shortUrl}?${qs}` : this.shortUrl;
    }
  },

  watch: {
    visible(val) {
      if (val) {
        // Reset fields when opened
        this.utmSource = '';
        this.utmMedium = '';
        this.utmCampaign = '';
        this.utmContent = '';
        this.copied = false;
      }
    }
  },

  methods: {
    toggleChip(field, value) {
      if (this[field] === value) {
        this[field] = '';
      } else {
        this[field] = value;
      }
    },

    async copyLink() {
      const url = this.previewUrl;
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.copied = true;
      this.$emit('copied');
      setTimeout(() => { this.copied = false; }, 2000);
    },

    close() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.utm-short-url-display {
  background: #f0f4ff;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  padding: 8px 12px;
  font-family: monospace;
  font-size: 13px;
  color: #4338ca;
  word-break: break-all;
}

.utm-section-divider {
  display: flex;
  align-items: center;
  margin: 16px 0 12px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.utm-section-divider::before,
.utm-section-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

.utm-section-divider::before {
  margin-right: 10px;
}

.utm-section-divider::after {
  margin-left: 10px;
}

.utm-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.utm-chip {
  padding: 3px 10px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: #fff;
  font-size: 12px;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s;
}

.utm-chip:hover {
  border-color: #6366f1;
  color: #6366f1;
}

.utm-chip-active {
  background: #6366f1;
  border-color: #6366f1;
  color: #fff;
}

.utm-chip-active:hover {
  background: #4f46e5;
  color: #fff;
}

.utm-preview-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 12px;
  min-height: 36px;
}

.utm-preview-url {
  font-family: monospace;
  font-size: 12px;
  color: #059669;
  word-break: break-all;
}
</style>
